
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import axios from "axios";
import { useNavigate } from "react-router-dom";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import HomeIcon from "@mui/icons-material/Home";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";

import useUser from "../hooks/useUser";

const SERVIDOR = "https://centroformularioscixsa.com";

const baseCampo = {
  "& .MuiInputBase-root": {
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
  },
  "& .MuiInputBase-input": {
    color: "#3d5fd1",
    fontWeight: 600,
  },
  "& .MuiInputLabel-root": {
    color: "#718b47",
    fontWeight: 600,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#8292da",
  },
};

// ==========================================
// FUNCIONES AUXILIARES
// ==========================================

const leer = (obj, ...nombres) => {
  if (!obj || typeof obj !== "object") return "";

  const claves = Object.keys(obj);

  for (const nombre of nombres) {
    const clave = claves.find(
      (k) => k.toLowerCase() === nombre.toLowerCase()
    );

    if (
      clave &&
      obj[clave] != null &&
      String(obj[clave]).trim() !== ""
    ) {
      return String(obj[clave]);
    }
  }

  return "";
};

const filas = (data) => {
  if (Array.isArray(data)) return data;

  for (const clave of ["resultado", "body", "data"]) {
    if (Array.isArray(data?.[clave])) {
      return data[clave];
    }
  }

  return [];
};

const catalogo = (data, claveId, ...clavesNombre) =>
  filas(data).map((raw, i) => ({
    id: leer(raw, claveId) || `sin-id-${i}`,
    nombre:
      leer(raw, ...clavesNombre) ||
      `Sin descripción ${i + 1}`,
    raw,
  }));

const comoId = (id) =>
  /^\d+$/.test(String(id)) ? Number(id) : id;

const sinDatos = (msg) =>
  /no se ha|no se han|no existen|no hay|no se encontr|ning[uú]n|sin resultados/i.test(
    msg || ""
  );

const normal = (s) =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const distintos = (items) => [
  ...new Map(
    items
      .filter((x) => !x.id.startsWith("sin-id-"))
      .map((x) => [x.id, x])
  ).values(),
];

// ==========================================
// COMPONENTE PRINCIPAL
// ==========================================

export default function MantenimientoRespuestas() {
  const navigate = useNavigate();
  const user = useUser();

  const apiBase = `${SERVIDOR}${user?.puerto ?? ""}`;
  const generacion = useRef(0);

  // CATÁLOGOS
  const [periodos, setPeriodos] = useState([]);
  const [formularios, setFormularios] = useState([]);
  const [versiones, setVersiones] = useState([]);
  const [secciones, setSecciones] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [preguntas, setPreguntas] = useState([]);
  const [respuestas, setRespuestas] = useState([]);

  // ESTADO DE CONSULTA
  const [estadoRespuestas, setEstadoRespuestas] =
    useState("sin-consultar");

  // VALORES SELECCIONADOS
  const [periodo, setPeriodo] = useState("");
  const [formulario, setFormulario] = useState("");
  const [version, setVersion] = useState("");
  const [seccion, setSeccion] = useState("");
  const [grupo, setGrupo] = useState("");
  const [pregunta, setPregunta] = useState("");

  // ESTADOS GENERALES
  const [cargando, setCargando] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [modal, setModal] = useState(false);
  const [nuevaRespuesta, setNuevaRespuesta] = useState("");
  const [guardando, setGuardando] = useState(false);

  const [aviso, setAviso] = useState({
    open: false,
    mensaje: "",
    tipo: "info",
  });

  // ==========================================
  // NOTIFICACIONES
  // ==========================================

  const notificar = (mensaje, tipo = "info") => {
    setAviso({
      open: true,
      mensaje: String(mensaje),
      tipo,
    });
  };

  const nuevaConsulta = () => ++generacion.current;

  const vigente = (token) =>
    token === generacion.current;

  // ==========================================
  // CONSUMIR API
  // ==========================================

  const post = useCallback(
    async (ruta, body = {}, aceptaVacio = true) => {
      const { data } = await axios.post(
        `${apiBase}${ruta}`,
        body,
        {
          headers: {
            "Content-Type": "application/json",
          },
          timeout: 20000,
        }
      );

      if (data?.error) {
        const mensaje = String(
          data.message || "Error del servicio"
        );

        if (aceptaVacio && sinDatos(mensaje)) {
          return [];
        }

        throw new Error(mensaje);
      }

      return aceptaVacio ? filas(data) : data;
    },
    [apiBase]
  );

  // ==========================================
  // LIMPIAR SELECTORES DEPENDIENTES
  // ==========================================

  const limpiarDesde = (nivel) => {
    if (nivel <= 1) {
      setFormulario("");
      setFormularios([]);
    }

    if (nivel <= 2) {
      setVersion("");
      setVersiones([]);
    }

    if (nivel <= 3) {
      setSeccion("");
      setSecciones([]);
    }

    if (nivel <= 4) {
      setGrupo("");
      setGrupos([]);
    }

    if (nivel <= 5) {
      setPregunta("");
      setPreguntas([]);
    }

    setRespuestas([]);
    setEstadoRespuestas("sin-consultar");
    setBusqueda("");
    setNuevaRespuesta("");
    setModal(false);
  };

  // ==========================================
  // CONSULTAR PERÍODOS
  // ==========================================

  const cargarPeriodos = useCallback(async () => {
    const token = ++generacion.current;

    setCargando("períodos");

    try {
      const data = await post(
        "/periodo/periodo/",
        {}
      );

      if (token === generacion.current) {
        setPeriodos(
          catalogo(
            data,
            "id_periodo",
            "desc_periodo"
          )
        );
      }
    } catch (e) {
      if (token === generacion.current) {
        notificar(
          `Períodos: ${e.message}`,
          "error"
        );
      }
    } finally {
      if (token === generacion.current) {
        setCargando("");
      }
    }
  }, [post]);

  useEffect(() => {
    if (user?.puerto != null) {
      cargarPeriodos();
    }

    return () => {
      generacion.current += 1;
    };
  }, [user?.puerto, cargarPeriodos]);

  // ==========================================
  // CAMBIAR PERÍODO
  // ==========================================

  const cambiarPeriodo = async (id) => {
    const token = nuevaConsulta();

    setPeriodo(id);
    limpiarDesde(1);
    setCargando("");

    if (!id) return;

    const elegido = periodos.find(
      (x) => x.id === id
    );

    if (!elegido) return;

    setCargando("formularios");

    try {
      const data = await post(
        "/formulario_periodo/formulario_periodo/",
        {
          desc_periodo: elegido.nombre,
        }
      );

      if (vigente(token)) {
        setFormularios(
          catalogo(
            data,
            "id_formulario",
            "titulo"
          )
        );
      }
    } catch (e) {
      if (vigente(token)) {
        notificar(
          `Formularios: ${e.message}`,
          "error"
        );
      }
    } finally {
      if (vigente(token)) {
        setCargando("");
      }
    }
  };

  // ==========================================
  // CAMBIAR VERSIÓN
  // ==========================================

  const cambiarVersion = async (
    formId,
    verId,
    tokenAnterior
  ) => {
    const token = tokenAnterior ?? nuevaConsulta();

    setVersion(verId);
    limpiarDesde(3);
    setCargando("");

    if (!verId) return;

    setCargando("secciones");

    try {
      const data = await post(
        "/formulario_periodo/formulario_seccion/",
        {
          id_formulario: comoId(formId),
          id_version: comoId(verId),
        }
      );

      if (vigente(token)) {
        setSecciones(
          catalogo(
            data,
            "id_seccion",
            "desc_seccion"
          )
        );
      }
    } catch (e) {
      if (vigente(token)) {
        notificar(
          `Secciones: ${e.message}`,
          "error"
        );
      }
    } finally {
      if (vigente(token)) {
        setCargando("");
      }
    }
  };

  // ==========================================
  // CAMBIAR FORMULARIO
  // ==========================================

  const cambiarFormulario = async (id) => {
    const token = nuevaConsulta();

    setFormulario(id);
    limpiarDesde(2);
    setCargando("");

    if (!id) return;

    setCargando("versiones");

    try {
      const data = await post(
        "/formulario_periodo/formulario_id_formulario/",
        {
          id_formulario: comoId(id),
        }
      );

      if (!vigente(token)) return;

      const lista = catalogo(
        data,
        "id_version",
        "desc_version",
        "version"
      );

      setVersiones(lista);

      if (lista.length === 1) {
        await cambiarVersion(
          id,
          lista[0].id,
          token
        );
      }
    } catch (e) {
      if (vigente(token)) {
        notificar(
          `Versiones: ${e.message}`,
          "error"
        );
      }
    } finally {
      if (vigente(token)) {
        setCargando("");
      }
    }
  };

  // ==========================================
  // CAMBIAR SECCIÓN
  // ==========================================

  const cambiarSeccion = async (id) => {
    const token = nuevaConsulta();

    setSeccion(id);
    limpiarDesde(4);
    setCargando("");

    if (!id) return;

    setCargando("grupos de preguntas");

    try {
      const data = await post(
        "/formulario_sin_finalizar/obtener_grupo_pregunta/",
        {
          id_formulario: comoId(formulario),
          id_seccion: comoId(id),
        }
      );

      if (!vigente(token)) return;

      const validos = data.filter(
        (r) =>
          (
            !leer(r, "id_formulario") ||
            leer(r, "id_formulario") ===
              String(formulario)
          ) &&
          (
            !leer(r, "id_seccion") ||
            leer(r, "id_seccion") ===
              String(id)
          ) &&
          (
            !leer(r, "id_version") ||
            leer(r, "id_version") ===
              String(version)
          )
      );

      const lista = distintos(
        catalogo(
          validos,
          "id_grupo_pregunta",
          "desc_grupo_pregunta"
        )
      );

      setGrupos(lista);

      if (!lista.length) {
        notificar(
          "No hay grupos de preguntas para esta sección.",
          "warning"
        );
      }
    } catch (e) {
      if (vigente(token)) {
        notificar(
          `Grupos de preguntas: ${e.message}`,
          "error"
        );
      }
    } finally {
      if (vigente(token)) {
        setCargando("");
      }
    }
  };

  // ==========================================
  // CAMBIAR GRUPO
  // ==========================================

  const cambiarGrupo = async (id) => {
    const token = nuevaConsulta();

    setGrupo(id);
    limpiarDesde(5);
    setCargando("");

    if (!id) return;

    setCargando("preguntas del grupo");

    try {
      const data = await post(
        "/formulario_sin_finalizar/obtener_pregunta/",
        {
          id_formulario: comoId(formulario),
          id_seccion: comoId(seccion),
        }
      );

      if (!vigente(token)) return;

      if (
        data.length &&
        !Object.keys(data[0]).some(
          (k) =>
            k.toLowerCase() ===
            "id_grupo_pregunta"
        )
      ) {
        throw new Error(
          "La vista getpreguntasformulario debe incluir " +
          "id_grupo_pregunta y el JOIN de preguntas con etiquetas."
        );
      }

      const validos = data.filter(
        (r) =>
          leer(r, "id_formulario") ===
            String(formulario) &&

          leer(r, "id_seccion") ===
            String(seccion) &&

          leer(r, "id_grupo_pregunta") ===
            String(id) &&

          (
            !leer(r, "id_version") ||
            leer(r, "id_version") ===
              String(version)
          ) &&

          leer(r, "estado_pregunta") !== "I"
      );

      const lista = distintos(
        catalogo(
          validos,
          "id_pregunta",
          "desc_pregunta",
          "descripcion_pregunta"
        )
      ).sort((a, b) =>
        a.nombre.localeCompare(b.nombre, "es")
      );

      setPreguntas(lista);

      if (!lista.length) {
        notificar(
          "Este grupo no tiene preguntas activas.",
          "info"
        );
      }
    } catch (e) {
      if (vigente(token)) {
        notificar(
          `Preguntas: ${e.message}`,
          "error"
        );
      }
    } finally {
      if (vigente(token)) {
        setCargando("");
      }
    }
  };

  // ==========================================
  // CONSULTAR RESPUESTAS
  // ==========================================

  const consultarRespuestas = async (
    id,
    sinAviso = false
  ) => {
    const token = nuevaConsulta();

    setPregunta(id);
    setRespuestas([]);
    setEstadoRespuestas("sin-consultar");
    setBusqueda("");
    setNuevaRespuesta("");
    setModal(false);
    setCargando("");

    if (!id) return;

    setEstadoRespuestas("consultando");
    setCargando("respuestas");

    try {
      const data = await post(
        "/formulario_sin_finalizar/obtener_respuesta/",
        {
          id_formulario: comoId(formulario),
          id_seccion: comoId(seccion),
          id_pregunta: comoId(id),
        }
      );

      if (!vigente(token)) return;

      const validos = data.filter(
        (r) =>
          (
            !leer(r, "id_formulario") ||
            leer(r, "id_formulario") ===
              String(formulario)
          ) &&
          (
            !leer(r, "id_seccion") ||
            leer(r, "id_seccion") ===
              String(seccion)
          ) &&
          (
            !leer(r, "id_pregunta") ||
            leer(r, "id_pregunta") ===
              String(id)
          )
      );

      const lista = distintos(
        catalogo(
          validos,
          "id_respuesta",
          "desc_respuesta",
          "descripcion_respuesta"
        )
      );

      setRespuestas(lista);
      setEstadoRespuestas("completada");

      if (!lista.length && !sinAviso) {
        notificar(
          "Pregunta abierta: no tiene opciones de " +
          "respuesta y no permite agregar nuevas.",
          "info"
        );
      }
    } catch (e) {
      if (vigente(token)) {
        setEstadoRespuestas("error");

        notificar(
          `Respuestas: ${e.message}`,
          "error"
        );
      }
    } finally {
      if (vigente(token)) {
        setCargando("");
      }
    }
  };

  // ==========================================
  // GUARDAR NUEVA RESPUESTA
  // ==========================================

  const guardarRespuesta = async () => {
    if (guardando) return;

    const descripcion = nuevaRespuesta
      .trim()
      .replace(/\s+/g, " ");

    if (
      !formulario ||
      !seccion ||
      !grupo ||
      !pregunta
    ) {
      notificar(
        "Seleccione formulario, sección, grupo y pregunta.",
        "warning"
      );
      return;
    }

    if (!descripcion) {
      notificar(
        "Ingrese la descripción de la nueva respuesta.",
        "warning"
      );
      return;
    }

    if (descripcion.length > 255) {
      notificar(
        "La descripción no puede superar los 255 caracteres.",
        "warning"
      );
      return;
    }

    if (
      respuestaAbierta ||
      estadoRespuestas !== "completada"
    ) {
      notificar(
        "No se permite agregar opciones a preguntas abiertas " +
        "o sin respuestas verificadas.",
        "warning"
      );
      return;
    }

    const duplicada = respuestas.some(
      (r) =>
        normal(r.nombre) === normal(descripcion)
    );

    if (duplicada) {
      notificar(
        "Esta respuesta ya existe para la pregunta seleccionada.",
        "warning"
      );
      return;
    }

    const token = generacion.current;

    setGuardando(true);

    try {
      // NUEVO ENDPOINT DEL CATÁLOGO MAESTRO
      const confirmacion = await post(
        "/formulario_sin_finalizar/catalogo_respuesta_agregar/",
        {
          id_formulario: comoId(formulario),
          id_seccion: comoId(seccion),
          id_pregunta: comoId(pregunta),
          descripcion_respuesta: descripcion,
        },
        false
      );

      if (!vigente(token)) return;

      // COMPROBAR LA CONFIRMACIÓN DEL BACKEND
      const insertados = filas(confirmacion);

      if (!insertados.length) {
        throw new Error(
          "El servicio no confirmó la inserción de la respuesta."
        );
      }

      setModal(false);
      setNuevaRespuesta("");

      notificar(
        "Respuesta agregada satisfactoriamente.",
        "success"
      );

      // ACTUALIZAR LISTADO DE RESPUESTAS
      await consultarRespuestas(pregunta, true);

    } catch (e) {
      if (vigente(token)) {
        notificar(
          `Guardar respuesta: ${e.message}`,
          "error"
        );
      }
    } finally {
      setGuardando(false);
    }
  };

  // ==========================================
  // CERRAR SESIÓN
  // ==========================================

  const cerrarSesion = () => {
    nuevaConsulta();

    sessionStorage.clear();
    sessionStorage.setItem("token", null);

    if (user) {
      user.codigo_usuario = null;
    }

    navigate("/login", {
      replace: true,
    });
  };

  // ==========================================
  // INFORMACIÓN DE LA INTERFAZ
  // ==========================================

  const respuestasFiltradas = useMemo(
    () =>
      respuestas.filter((r) =>
        normal(r.nombre).includes(
          normal(busqueda)
        )
      ),
    [respuestas, busqueda]
  );

  const seleccionado = (lista, id) =>
    lista.find((x) => x.id === id);

  const preguntaElegida = seleccionado(
    preguntas,
    pregunta
  );

  // NO PERMITIR AGREGAR EN PREGUNTAS ABIERTAS
  const respuestaAbierta =
    Boolean(pregunta) &&
    (
      leer(
        preguntaElegida?.raw,
        "id_tipo_pregunta"
      ) === "1" ||
      (
        estadoRespuestas === "completada" &&
        respuestas.length === 0
      )
    );

  const ocupado =
    Boolean(cargando) || guardando;

  // ==========================================
  // SELECTOR REUTILIZABLE
  // ==========================================

  const selector = (
    titulo,
    valor,
    lista,
    cambiar,
    deshabilitado,
    key
  ) => (
    <TextField
      key={key}
      fullWidth
      select
      label={titulo}
      value={valor}
      sx={baseCampo}
      disabled={deshabilitado}
      onChange={(e) =>
        cambiar(e.target.value)
      }
    >
      <MenuItem value="">
        Seleccione {titulo.toLowerCase()}
      </MenuItem>

      {lista.map((item) => (
        <MenuItem
          key={item.id}
          value={item.id}
        >
          {item.nombre}
        </MenuItem>
      ))}
    </TextField>
  );

  // ==========================================
  // INTERFAZ
  // ==========================================

  return (
    <Container
      maxWidth="xl"
      sx={{
        p: 2,
        pb: 8,
        bgcolor: "#f7f9fc",
        minHeight: "100vh",
      }}
    >
      {/* ENCABEZADO */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              bgcolor: "green",
              borderRadius: 2,
              p: 2,
              textAlign: "center",
              flex: "1 1 580px",
            }}
          >
            <Typography
              variant="h4"
              color="white"
              fontWeight="bold"
            >
              MANTENIMIENTO DE RESPUESTAS
            </Typography>

            <Typography color="white">
              DATOS MAESTROS · GRUPO DE PREGUNTAS
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              startIcon={<HomeIcon />}
              onClick={() =>
                navigate("/menu-categorias")
              }
            >
              Menú Principal
            </Button>

            <Button
              variant="contained"
              color="success"
              startIcon={<ExitToAppIcon />}
              onClick={cerrarSesion}
            >
              Cerrar Sesión
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* SELECCIONAR FORMULARIO */}
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          mb: 3,
          borderColor: "#0c0034",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography
            variant="h6"
            fontWeight="bold"
          >
            Seleccionar formulario
          </Typography>

          <Button
            startIcon={<RefreshIcon />}
            disabled={ocupado}
            onClick={() => {
              nuevaConsulta();
              setPeriodo("");
              limpiarDesde(1);
              cargarPeriodos();
            }}
          >
            Actualizar períodos
          </Button>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "1fr 3fr 1.5fr",
            },
            gap: 2,
          }}
        >
          {selector(
            "Período",
            periodo,
            periodos,
            cambiarPeriodo,
            ocupado || !periodos.length,
            "periodos"
          )}

          {selector(
            "Formulario",
            formulario,
            formularios,
            cambiarFormulario,
            ocupado ||
              !periodo ||
              !formularios.length,
            `form-${periodo}`
          )}

          {selector(
            "Versión",
            version,
            versiones,
            (id) =>
              cambiarVersion(formulario, id),
            ocupado ||
              !formulario ||
              !versiones.length,
            `ver-${periodo}-${formulario}`
          )}
        </Box>
      </Paper>

      {/* SECCIÓN, GRUPO Y PREGUNTA */}
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          mb: 3,
          borderColor: "#0c0034",
        }}
      >
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ mb: 2 }}
        >
          Seleccionar pregunta
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              md: "repeat(2, minmax(0, 1fr))",
            },
            gap: 2,
            width: "100%",
            alignItems: "start",
          }}
        >
          {/* SECCIÓN */}
          <Box sx={{ minWidth: 0 }}>
            {selector(
              "Sección",
              seccion,
              secciones,
              cambiarSeccion,
              ocupado ||
                !version ||
                !secciones.length,
              `sec-${formulario}-${version}`
            )}
          </Box>

          {/* GRUPO DE PREGUNTAS */}
          <Box sx={{ minWidth: 0 }}>
            {selector(
              "Grupo de preguntas",
              grupo,
              grupos,
              cambiarGrupo,
              ocupado ||
                !seccion ||
                !grupos.length,
              `grupo-${formulario}-${version}-${seccion}`
            )}
          </Box>

          {/* PREGUNTA */}
          <Box
            sx={{
              gridColumn: "1 / -1",
              minWidth: 0,
            }}
          >
            {selector(
              "Pregunta",
              pregunta,
              preguntas,
              consultarRespuestas,
              ocupado ||
                !grupo ||
                !preguntas.length,
              `pre-${formulario}-${version}-${seccion}-${grupo}`
            )}
          </Box>
        </Box>

        {cargando && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              mt: 2,
            }}
          >
            <CircularProgress size={19} />

            <Typography variant="body2">
              Consultando {cargando}...
            </Typography>
          </Box>
        )}

        {preguntaElegida && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {seleccionado(
              formularios,
              formulario
            )?.nombre}

            {" · "}

            {seleccionado(
              secciones,
              seccion
            )?.nombre}

            {" · "}

            {seleccionado(
              grupos,
              grupo
            )?.nombre}

            <Typography fontWeight="bold">
              {preguntaElegida.nombre}
            </Typography>
          </Alert>
        )}
      </Paper>

      {/* RESPUESTAS DISPONIBLES */}
      <Typography
        variant="h6"
        sx={{
          bgcolor: "#0c0034",
          color: "white",
          p: 1.5,
          textAlign: "center",
          borderRadius: 1,
        }}
      >
        RESPUESTAS DISPONIBLES
      </Typography>

      <Paper
        sx={{
          mt: 1,
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography fontWeight="bold">
            Total de respuestas
          </Typography>

          <Chip
            label={respuestas.length}
            color="primary"
            size="small"
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <TextField
            size="small"
            label="Buscar respuesta"
            value={busqueda}
            disabled={!pregunta || ocupado}
            onChange={(e) =>
              setBusqueda(e.target.value)
            }
          />

          <Button
            variant="outlined"
            startIcon={<SearchIcon />}
            disabled={!pregunta || ocupado}
            onClick={() =>
              consultarRespuestas(pregunta, true)
            }
          >
            Consultar
          </Button>

          {/* BOTÓN PARA AGREGAR */}
          <Button
            variant="contained"
            color="success"
            startIcon={<AddIcon />}
            disabled={
              !pregunta ||
              ocupado ||
              estadoRespuestas !== "completada" ||
              respuestaAbierta
            }
            onClick={() => {
              setNuevaRespuesta("");
              setModal(true);
            }}
          >
            Agregar respuesta
          </Button>
        </Box>
      </Paper>

      {/* AVISO DE PREGUNTA ABIERTA */}
      {respuestaAbierta && (
        <Alert
          severity="info"
          sx={{ mt: 1 }}
        >
          <strong>Pregunta abierta.</strong>{" "}
          No contiene opciones de respuesta y no se
          permite agregar respuestas a su catálogo.
        </Alert>
      )}

      {/* TABLA DE RESPUESTAS */}
      <TableContainer
        component={Paper}
        sx={{
          mt: 1,
          mb: 4,
          maxHeight: 500,
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  bgcolor: "#e9edf5",
                  fontWeight: "bold",
                }}
              >
                RESPUESTA
              </TableCell>

              <TableCell
                sx={{
                  bgcolor: "#e9edf5",
                  fontWeight: "bold",
                  width: 120,
                }}
              >
                ESTADO
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {respuestasFiltradas.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>
                  {r.nombre}
                </TableCell>

                <TableCell>
                  {leer(
                    r.raw,
                    "estado_respuesta",
                    "estado"
                  ) || "—"}
                </TableCell>
              </TableRow>
            ))}

            {!respuestasFiltradas.length && (
              <TableRow>
                <TableCell
                  colSpan={2}
                  align="center"
                  sx={{ py: 5 }}
                >
                  {!pregunta
                    ? "Seleccione una pregunta para consultar."
                    : respuestaAbierta
                    ? "Pregunta abierta: no hay opciones de respuesta que administrar."
                    : estadoRespuestas === "error"
                    ? "No fue posible consultar las respuestas. Intente nuevamente."
                    : "No se encontraron respuestas."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* MODAL NUEVA RESPUESTA */}
      <Dialog
        open={modal}
        maxWidth="sm"
        fullWidth
        onClose={
          guardando
            ? undefined
            : () => setModal(false)
        }
      >
        <DialogTitle>
          Agregar nueva respuesta
        </DialogTitle>

        <DialogContent dividers>
          <Typography
            variant="body2"
            sx={{ mb: 1 }}
          >
            <strong>Formulario:</strong>{" "}
            {seleccionado(
              formularios,
              formulario
            )?.nombre}
          </Typography>

          <Typography
            variant="body2"
            sx={{ mb: 1 }}
          >
            <strong>Sección:</strong>{" "}
            {seleccionado(
              secciones,
              seccion
            )?.nombre}
          </Typography>

          <Typography
            variant="body2"
            sx={{ mb: 1 }}
          >
            <strong>Grupo:</strong>{" "}
            {seleccionado(
              grupos,
              grupo
            )?.nombre}
          </Typography>

          <Typography
            variant="body2"
            sx={{ mb: 2 }}
          >
            <strong>Pregunta:</strong>{" "}
            {preguntaElegida?.nombre}
          </Typography>

          <TextField
            fullWidth
            autoFocus
            multiline
            minRows={2}
            label="Descripción de la nueva respuesta"
            inputProps={{
              maxLength: 255,
            }}
            value={nuevaRespuesta}
            sx={baseCampo}
            onChange={(e) =>
              setNuevaRespuesta(e.target.value)
            }
          />
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            disabled={guardando}
            onClick={() => setModal(false)}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            color="success"
            disabled={
              guardando ||
              respuestaAbierta ||
              estadoRespuestas !== "completada" ||
              !nuevaRespuesta.trim()
            }
            startIcon={
              guardando ? (
                <CircularProgress
                  size={18}
                  color="inherit"
                />
              ) : (
                <AddIcon />
              )
            }
            onClick={guardarRespuesta}
          >
            Guardar respuesta
          </Button>
        </DialogActions>
      </Dialog>

      {/* NOTIFICACIONES */}
      <Snackbar
        open={aviso.open}
        autoHideDuration={6000}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        onClose={() =>
          setAviso((a) => ({
            ...a,
            open: false,
          }))
        }
      >
        <Alert
          severity={aviso.tipo}
          variant="filled"
          sx={{ width: "100%" }}
          onClose={() =>
            setAviso((a) => ({
              ...a,
              open: false,
            }))
          }
        >
          {aviso.mensaje}
        </Alert>
      </Snackbar>
    </Container>
  );
}
